import { TableLocal, TableLocalPagination } from "components/Common/TableLocal"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Badge } from "reactstrap"
import { CanView } from "components/Common/CanView"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { downloadAndOpenFile } from "api/pdfApi"
import {
  useCancelMoneyReceipt,
  useDeleteMoneyReceipt,
} from "hooks/useMoneyReceipt"
import { useToastMessage } from "hooks/useToastMessage"
import { useListParams } from "hooks/useListParams"
import ConfirmModal from "components/Common/ConfirmModal"
import DeleteModal from "components/Common/DeleteModal"
import { ACCESS } from "constants/accessPermissions"

const paymentBadgeColor = status => {
  if (status === "overpaid") return "primary"
  if (status === "fully_paid") return "success"
  if (status === "partially_paid") return "info"
  if (status === "refunded") return "secondary"
  return "warning"
}

const getReceiptCustomerName = item =>
  item?.pdf_content?.customer_info?.name || item?.customer?.name || "-"

const getReceiptCustomerMobile = item =>
  item?.pdf_content?.customer_info?.mobile || item?.customer?.mobile || "-"

const getReceiptVehicleLabel = item =>
  item?.pdf_content?.vehicle_info?.name ||
  item?.vehicle?.stock_id ||
  item?.pdf_content?.vehicle_info?.stock_id ||
  "-"

const getReceiptVehicleIdentity = item =>
  item?.pdf_content?.vehicle_info?.full_chassis ||
  item?.pdf_content?.vehicle_info?.chassis_no ||
  item?.vehicle?.full_chassis ||
  "-"

const getReceiptVehicleMeta = item =>
  [
    item?.pdf_content?.vehicle_info?.year_model,
    item?.pdf_content?.vehicle_info?.color,
    item?.pdf_content?.vehicle_info?.fuel,
  ]
    .filter(Boolean)
    .join(" | ")

const MoneyReceiptList = ({ data, onChanged }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { handleResponse, handleError } = useToastMessage()
  const { params, updateParams } = useListParams({
    mr_page: 1,
    mr_per_page: 5,
  })
  const currentPage = Number(params?.mr_page) || 1
  const itemsPerPage = Number(params?.mr_per_page) || 5
  const focusId = params?.mr_focus ?? null
  const focusHandledRef = useRef(false)
  const focusRowRef = useRef(null)
  const [deleteModal, setDeleteModal] = useState(null)
  const [cancelModal, setCancelModal] = useState(null)

  const { mutateAsync: deleteMoneyReceipt, isPending: isDeleting } =
    useDeleteMoneyReceipt()
  const { mutateAsync: cancelMoneyReceipt, isPending: isCancelling } =
    useCancelMoneyReceipt()

  const sortedContents = useMemo(() => {
    if (!data) return []
    const contents = Array.isArray(data) ? data : []
    return [...contents].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )
  }, [data])

  const handlePageChange = useCallback(
    page => updateParams({ mr_page: page }),
    [updateParams]
  )
  const handleItemsPerPageChange = useCallback(
    n => updateParams({ mr_per_page: n, mr_page: 1 }),
    [updateParams]
  )

  useEffect(() => {
    if (!focusId || focusHandledRef.current || !sortedContents.length) return
    const idx = sortedContents.findIndex(
      it =>
        String(it.id) === String(focusId) ||
        String(it.mr_no) === String(focusId)
    )
    if (idx === -1) return
    const targetPage = Math.floor(idx / itemsPerPage) + 1
    if (targetPage !== currentPage) {
      updateParams({ mr_page: targetPage })
    }
    focusHandledRef.current = true
  }, [focusId, sortedContents, itemsPerPage, currentPage, updateParams])

  useEffect(() => {
    if (!focusId) return
    const node = focusRowRef.current
    if (!node) return
    node.scrollIntoView({ behavior: "smooth", block: "center" })
    node.classList.add("table-warning")
    const timer = setTimeout(() => {
      node.classList.remove("table-warning")
      updateParams({ mr_focus: undefined })
    }, 2500)
    return () => clearTimeout(timer)
  }, [focusId, currentPage, sortedContents, updateParams])

  const handleDelete = async item => {
    try {
      const response = await deleteMoneyReceipt({ id: item.id })
      handleResponse(response, "Money receipt deleted successfully.", true)
      setDeleteModal(null)
      onChanged?.()
    } catch (error) {
      handleError(error, "Failed to delete money receipt.")
    }
  }

  const handleCancel = async item => {
    try {
      await cancelMoneyReceipt(item.id)
      setCancelModal(null)
      onChanged?.()
    } catch (error) {
      handleError(error, "Failed to cancel money receipt.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Receipt",
        cell: item => (
          <div>
            <div className="fw-bold text-primary">{item?.mr_no ?? "-"}</div>
            <div className="small text-muted">
              Issued: {formatReadableDate(item?.issued_date, "MMM d, y")}
            </div>
            <div className="small text-muted">
              Quotation: {item?.quotation?.qid ?? "-"}
            </div>
          </div>
        ),
      },
      {
        header: "Customer",
        cell: item => (
          <div>
            <div className="fw-semibold">{getReceiptCustomerName(item)}</div>
            <div className="small text-muted">
              {getReceiptCustomerMobile(item)}
            </div>
          </div>
        ),
      },
      {
        header: "Vehicle",
        cell: item => (
          <div>
            <div>{getReceiptVehicleLabel(item)}</div>
            <div className="small text-muted">
              {getReceiptVehicleIdentity(item)}
            </div>
            {getReceiptVehicleMeta(item) ? (
              <div className="small text-muted">
                {getReceiptVehicleMeta(item)}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        header: "Financial",
        cell: item => {
          const carPrice = Number(item?.car_price ?? 0)
          const regCost = Number(item?.registration_cost ?? 0)
          const addlCost = Number(item?.additional_cost_total ?? 0)
          const discount = Number(
            item?.pdf_content?.vehicle_info?.discount ?? 0
          )
          return (
            <div>
              <div className="small text-muted">
                Price: {formatBDT(carPrice)}
              </div>
              {regCost > 0 && (
                <div className="small text-muted">
                  Reg: {formatBDT(regCost)}
                </div>
              )}
              {addlCost > 0 && (
                <div className="small text-muted">
                  Add'l: {formatBDT(addlCost)}
                </div>
              )}
              {discount > 0 && (
                <div className="small text-danger">
                  Discount: -{formatBDT(discount)}
                </div>
              )}
              <div className="fw-bold border-top pt-1 mt-1">
                Total: {formatBDT(item?.total_amount)}
              </div>
              <div className="small text-success">
                Paid: {formatBDT(item?.amount_paid)}
              </div>
              <div className="small text-muted">
                Due: {formatBDT(item?.due_amount)}
              </div>
              {Number(item?.overpaid_amount ?? 0) > 0 && (
                <div className="small text-warning">
                  Overpaid: {formatBDT(item?.overpaid_amount)}
                </div>
              )}
            </div>
          )
        },
      },
      {
        header: "Status",
        cell: item => (
          <div className="d-flex flex-column gap-1">
            <Badge color={item?.status === "active" ? "success" : "secondary"}>
              {item?.status ?? "active"}
            </Badge>
            <Badge color={paymentBadgeColor(item?.payment_status)}>
              {String(item?.payment_status ?? "pending").replaceAll("_", " ")}
            </Badge>
          </div>
        ),
      },
      {
        header: "Action",
        cell: item => {
          const canEdit = item?.status === "active"
          const canCancel = item?.status === "active"
          const canDelete = Number(item?.amount_paid ?? 0) === 0 && !isDeleting

          return (
            <ul className="list-unstyled hstack gap-1 mb-0 flex-wrap">
              <CanView access={[ACCESS.MONEY_RECEIPT.VIEW]}>
                <li>
                  <ButtonTooltip
                    type="view"
                    btnId={`mr-view-${item.id}`}
                    onClick={() => navigate(`/money-receipts/${item.id}`)}
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.MONEY_RECEIPT.EDIT]}>
                <li>
                  <ButtonTooltip
                    type="edit"
                    color="warning"
                    btnId={`mr-edit-${item.id}`}
                    disabled={!canEdit}
                    onClick={() =>
                      navigate(`/money-receipts/${item.id}/edit`, {
                        state: { from: location },
                      })
                    }
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.MONEY_RECEIPT.VIEW]}>
                <li>
                  <ButtonTooltip
                    type="pdf"
                    color="info"
                    btnId={`mr-pdf-${item.id}`}
                    onClick={() =>
                      downloadAndOpenFile({
                        url: `/money-receipts/${item.id}/pdf`,
                      })
                    }
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.MONEY_RECEIPT.CANCEL]}>
                <li>
                  <ButtonTooltip
                    type="cancel"
                    btnId={`mr-cancel-${item.id}`}
                    disabled={!canCancel || isCancelling}
                    onClick={() => setCancelModal(item)}
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.MONEY_RECEIPT.DELETE]}>
                <li>
                  <ButtonTooltip
                    type="delete"
                    btnId={`mr-delete-${item.id}`}
                    disabled={!canDelete}
                    onClick={() => setDeleteModal(item)}
                  />
                </li>
              </CanView>
            </ul>
          )
        },
      },
    ],
    [navigate, location, isDeleting, isCancelling]
  )

  return (
    <>
      <TableLocal
        columns={columns}
        data={sortedContents}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        tableClass="align-middle table-hover mb-0"
        getRowKey={row => row.id}
        rowProps={row => {
          const isFocus =
            focusId &&
            (String(row.id) === String(focusId) ||
              String(row.mr_no) === String(focusId))
          if (!isFocus) return {}
          return { ref: focusRowRef }
        }}
        emptyMessage={
          <div className="text-center py-5">
            <i className="bx bx-receipt bx-3x text-muted mb-3"></i>
            <h5 className="text-muted">No money receipts found</h5>
            <p className="text-muted mb-0">
              Create your first money receipt to get started
            </p>
          </div>
        }
      />

      <TableLocalPagination
        data={sortedContents}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        showItemsPerPage={true}
        itemsPerPageOptions={[5, 10, 20]}
        className="px-3 py-3 border-top"
      />
      <DeleteModal
        show={!!deleteModal}
        onDeleteClick={() => handleDelete(deleteModal)}
        onCloseClick={() => setDeleteModal(null)}
        loading={isDeleting}
      />

      <ConfirmModal
        isOpen={!!cancelModal}
        toggle={() => setCancelModal(null)}
        title="Cancel Money Receipt?"
        message="This will cancel the money receipt. Do you want to proceed?"
        confirmLabel="Cancel Receipt"
        icon="mdi-cancel"
        btnColor="warning"
        onConfirm={() => handleCancel(cancelModal)}
        isConfirming={isCancelling}
      />
    </>
  )
}

export default MoneyReceiptList
